import ScheduleList from "@/elements/components/schedule/schedule-list";
import ResponsiveContent from "@/elements/components/shared/responsive-content";
import { Tab } from "@/elements/components/ui/tab";
import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import React, { useCallback, useMemo, useState } from "react";
import { View, Text, Pressable } from "react-native";
import { api } from "@/elements/components/utils/convex-api";
import { Avatar } from "@/elements/components/ui/avatar";
import DownArrowIcon from "@/assets/svgs/down-arrow-icon";
import type { Id } from "@teachloop/backend/_generated/dataModel";
import { SheetIds } from "@/elements/components/utils/sheet-ids";
import type {
  ChildrenSelectSheetPayload,
  ChildrenSelectSheetResult,
} from "@/elements/components/sheets/children-select-sheet";
import { useSheet } from "@/elements/components/shared/sheet-provider";
import ScheduleListActiveIcon from "@/assets/svgs/schedule-list-active-icon";

function TutorSchedule() {
  const [statusFilter, setStatusFilter] = useState<
    "upcoming" | "requests" | "completed"
  >("upcoming");
  const [selectedStudentId, setSelectedStudentId] = useState<
    string | undefined
  >(undefined);
  const fromTime = useMemo(() => {
    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);
    return startOfDay.getTime();
  }, []);
  const { data: scheduleList } = useQuery({
    ...convexQuery(api.tutor.schedule.queries.getTutorSchedule, {
      statusFilter,
      fromTime,
      studentId: selectedStudentId
        ? (selectedStudentId as Id<"students">)
        : undefined,
    }),
  });
  const { items, count } = scheduleList ?? { items: [], count: 0 };
  const { data: students = [] } = useQuery(
    convexQuery(api.tutor.schedule.queries.getTutorStudents, {})
  );
  const selectedStudent = students.find(
    (student) => student.id === selectedStudentId
  );
  const { open } = useSheet<
    ChildrenSelectSheetPayload,
    ChildrenSelectSheetResult
  >(SheetIds.CHILDREN_SELECT);

  const handleStudentSelect = useCallback(async () => {
    const result = await open({
      title: "Show lessons for",
      options: [
        {
          label: "All",
          value: "all",
          imageUrl: null,
        },
        ...students.map((student) => ({
          label: student.name,
          value: student.id,
          imageUrl: student.imageUrl,
        })),
      ],
      preSelectedOption: selectedStudentId || "all",
    });
    if (result.selectedOption) {
      setSelectedStudentId(
        (result.selectedOption as string) === "all"
          ? undefined
          : (result.selectedOption as string)
      );
    }
  }, [students, open, selectedStudentId]);

  return (
    <View className="flex-1 bg-teachLoop-creamBase">
      <ScheduleList
        scheduleList={items}
        statusFilter={statusFilter}
        header={
          <ResponsiveContent>
            <View className="h-3" />
            <View className="mx-5">
              <View className="gap-1">
                <Text className="font-zilla-slab-medium font-medium text-dark text-[32px] leading-[1.2]">
                  Lesson schedule
                </Text>
                <Text className="font-geologica-regular font-normal text-[20px] text-gray-800 leading-[1.5]">
                  All your lessons in one place
                </Text>
              </View>
              <View className="h-8" />
              <View className="flex-row gap-2">
                <Tab
                  intent={statusFilter === "upcoming" ? "active" : "inActive"}
                  onPress={() => setStatusFilter("upcoming")}
                >
                  <Tab.Text>Upcoming</Tab.Text>
                </Tab>
                <Tab
                  intent={statusFilter === "requests" ? "active" : "inActive"}
                  onPress={() => setStatusFilter("requests")}
                >
                  <Tab.Text>Requests</Tab.Text>
                </Tab>
                <Tab
                  intent={statusFilter === "completed" ? "active" : "inActive"}
                  onPress={() => setStatusFilter("completed")}
                >
                  <Tab.Text>Completed</Tab.Text>
                </Tab>
              </View>
              <View className="h-6" />

              <View className="flex-row gap-6">
                <View className="flex-row items-center">
                  {selectedStudent ? (
                    <Pressable
                      className="flex-row items-center gap-2 border border-gray-200 rounded-[15px] p-2"
                      onPress={handleStudentSelect}
                    >
                      <Avatar size="sm">
                        <Avatar.Content
                          size="sm"
                          labelSize="sm"
                          label={selectedStudent?.name ?? ""}
                          source={
                            selectedStudent?.imageUrl
                              ? { uri: selectedStudent.imageUrl }
                              : undefined
                          }
                        />
                      </Avatar>
                      <Text className="font-geologica-medium text-gray-800 text-[16px] leading-[1.5]">
                        {selectedStudent?.name ?? ""}
                      </Text>
                      <DownArrowIcon pathClassName="fill-teachLoop-textPrimary" />
                    </Pressable>
                  ) : (
                    <Pressable
                      className="flex-row items-center gap-2 border border-gray-200 rounded-[15px] p-2"
                      onPress={handleStudentSelect}
                    >
                      <View className="flex-row">
                        {students.slice(0, 3).map((student, index) => (
                          <View
                            key={student.id}
                            className={`
                              ${index !== 0 ? "-ml-2" : ""}`}
                            style={{ zIndex: -(students.length - index) }}
                          >
                            <Avatar size="sm">
                              <Avatar.Content
                                size="sm"
                                labelSize="sm"
                                label={student.name}
                                source={
                                  student.imageUrl
                                    ? { uri: student.imageUrl }
                                    : undefined
                                }
                              />
                            </Avatar>
                          </View>
                        ))}
                      </View>
                      <Text className="font-geologica-medium text-gray-800 text-[16px] leading-[1.5]">
                        All
                      </Text>
                      <DownArrowIcon pathClassName="fill-teachLoop-textPrimary" />
                    </Pressable>
                  )}
                </View>

                <View className="flex-row items-center gap-4">
                  <ScheduleListActiveIcon pathClassName="fill-teachLoop-textPrimary" />
                </View>
              </View>
            </View>

            <View className="h-6" />
            <View className="items-end mx-5">
              <View className="px-2 bg-teal-500 rounded-md">
                <Text className="font-geologica-medium font-medium text-xs text-white leading-[1.5] uppercase">
                  {count} Lessons in total
                </Text>
              </View>
            </View>
            <View className="h-2" />
            <View className="h-[1px] bg-gray-300" />
            <View className="h-6" />
          </ResponsiveContent>
        }
      />
    </View>
  );
}

export default TutorSchedule;
